// =============================================================================
// clients-beneficiaries-client.http.ts — Adaptateur HTTP RÉEL du ClientBeneficiariesClient (S33).
// =============================================================================
// Remplace le stub-first derrière la MÊME interface (ClientBeneficiariesClient) : l'écran consomme
// l'interface, l'impl HTTP est substituée sans toucher l'UI. Rend la feature « clients bénéficiaires »
// ATTEIGNABLE end-to-end (rattrapage faux-vert ARCH DBU wave-fournisseur).
//
// AUTORITÉ = API (NestJS, AdminSessionGuard, gate de diffusion fail-closed). Cet adaptateur ne porte
// AUCUNE règle métier : il sérialise l'intention (campaignId d'URL), parse la réponse et relaie le
// message d'erreur serveur. GET pur (lecture) : aucun CSRF (double-submit réservé aux mutations).
// Le masquage des noms est décidé côté serveur : quand la diffusion n'est pas autorisée, le DTO ne
// PORTE aucun nom (le front ne peut rien déduire). Cookie session httpOnly via `credentials: 'include'`.

import {
  ClientBeneficiariesApiError,
  type ClientBeneficiariesClient,
  type ClientBeneficiariesDiffusionDto,
} from '@/api/clients-beneficiaries-client';

/** Forme du corps d'erreur structuré NestJS (`{ statusCode, code?, message }`). */
interface StructuredErrorBody {
  readonly statusCode?: number;
  readonly code?: string;
  readonly message?: string;
}

/**
 * Adaptateur HTTP réel des clients bénéficiaires diffusés. `baseUrl` par défaut `/api` (parité des
 * autres clients admin). En test, `fetch` est mocké.
 */
export class HttpClientBeneficiariesClient implements ClientBeneficiariesClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  public async listBeneficiaries(
    campaignId: string,
  ): Promise<ClientBeneficiariesDiffusionDto> {
    const response = await fetch(
      `${this.baseUrl}/admin/clients/campaigns/${encodeURIComponent(campaignId)}/beneficiaries`,
      { method: 'GET', credentials: 'include', headers: { 'content-type': 'application/json' } },
    );
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as ClientBeneficiariesDiffusionDto;
  }

  /** Convertit une réponse d'erreur en ClientBeneficiariesApiError (message serveur, sinon repli). */
  private async toApiError(response: Response): Promise<ClientBeneficiariesApiError> {
    let body: StructuredErrorBody | undefined;
    try {
      body = (await response.json()) as StructuredErrorBody;
    } catch {
      body = undefined;
    }
    const message =
      body?.message ??
      `Erreur API (${String(response.status)}) sur les clients bénéficiaires.`;
    return new ClientBeneficiariesApiError(message, response.status);
  }
}
